import api from 'services/api';
import { REJECT, nullable, num, object, str, type Received } from 'validation';

import { readResponse } from 'api/shared/readResponse';

import { folderPathSchema, type FolderPath } from 'api/shared/folderPath';
import type { SavePythonLibrary } from './postPythonLibrary';

/**
 * `PUT /python_libraries/:id` → the `UPDATE … RETURNING` row plus `folder_path`.
 *
 * Its own type + schema, for the same reason as `CreatedPythonLibrary`: a distinct statement that
 * happens to select the same columns. Like the create response it omits `content`, so it cannot
 * re-seed an editor, and like it, the `documentation` it does return is deliberately not carried —
 * see `SavePythonLibrary`.
 */
export interface UpdatedPythonLibrary {
  readonly python_library_id: number;
  readonly name: string;
  readonly filename: string;
  readonly description: string | null;
  readonly hash: string;
  readonly folder_id: number | null;
  readonly created_at: string | null;
  readonly updated_at: string | null;
  readonly folder_path: FolderPath | null;
}

const updatedSchema = object<UpdatedPythonLibrary>({
  python_library_id: num(REJECT),
  name: str(),
  filename: str(),
  description: nullable(str()),
  hash: str(),
  folder_id: nullable(num()),
  created_at: nullable(str()),
  updated_at: nullable(str()),
  folder_path: nullable(folderPathSchema),
});

/**
 * PUT `/python_libraries/:id`. Returns the updated record.
 * Rejects on HTTP error — the update did not happen (404 when the library is gone, 400 on a
 * rejected body), and the server's complaint is the useful thing to report. Answers with the
 * **unreadable** arm when the update DID happen and its receipt cannot be read: by then the row
 * is committed, so that is not a failed save (#1035).
 *
 * Takes the full `SavePythonLibrary` — see that type for why every field must be sent, not only
 * the changed ones.
 */
export const putPythonLibrary = async (id: number, save: SavePythonLibrary): Promise<Received<UpdatedPythonLibrary>> => {
  const res = await api.put(`/python_libraries/${id}`, save);
  return readResponse(res.data, { schema: updatedSchema, source: 'putPythonLibrary' });
};
